/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { memo } from 'react';

import { Accordion, AccordionGroup, AccordionHeader, AccordionPanel, Button, Typography } from '@coursera/cds-core';
import { CloseIcon, InformativeIcon } from '@coursera/cds-icons';

import SearchFilterMobile from 'bundles/search-common/components/filters/SearchFilterMobile';
import { COST_FACET_KEY, COST_FILTER_VALUE_PAID } from 'bundles/search-common/constants/filter';
import { useOwnsCourseraLite } from 'bundles/search-common/providers/LearnerProvider';
import type { SearchActiveFilters, SearchFilterOption } from 'bundles/search-common/types';
import type { FacetConfig } from 'bundles/search-common/types/filter';

import _t from 'i18n!nls/search-common';

type Props = {
  facets: FacetConfig[];
  isHidden: boolean;
  toggleMobileFilterMenu: () => void;
  addFilters: (filters: string) => void;
  removeFilters: (filters: string) => void;
  clearFilters: () => void;
  showCPlusSupportText?: boolean;
  filters?: Record<string, SearchFilterOption[] | undefined>;
  eventingData: {
    activeFilters?: SearchActiveFilters;
    searchIndex: string;
    searchIndexPosition: number;
  };
};

const styles = {
  mobileFiltersModal: (isHidden: boolean) => css`
    background-color: var(--cds-color-neutral-background-primary);
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    overflow-y: scroll;
    height: 100%;
    z-index: 4000; /* layover header */
    display: ${isHidden ? 'none' : 'initial'};
  `,
  contentContainer: css`
    padding: 16px 16px 80px;
  `,
  header: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 16px;
  `,
  footer: css`
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    z-index: 3;
    background-color: var(--cds-color-neutral-background-primary);
    border-top: 1px solid var(--cds-color-grey-25);
    padding: 12px 0;
    display: flex;
    justify-content: space-evenly;
  `,

  infoIcon: css`
    color: var(--cds-color-blue-800) !important;
  `,

  hasSupportText: css`
    .cds-AccordionHeader-button {
      padding-bottom: var(--cds-spacing-100) !important;
    }
  `,
};

function getSupportPropsFromFilter(filter: FacetConfig) {
  let supportText;
  let supportTextProps;
  if (filter.getToolTip?.()) {
    supportText = filter.getToolTip();
    supportTextProps = { icon: <InformativeIcon color="interactive" css={styles.infoIcon} /> };
  }
  if (filter.getSupportText?.()) {
    supportText = filter.getSupportText();
  }
  return { supportText, supportTextProps };
}

export const SearchFiltersMobileModal = ({
  filters,
  facets,
  isHidden,
  addFilters,
  removeFilters,
  clearFilters,
  toggleMobileFilterMenu,
  showCPlusSupportText,
  eventingData,
}: Props) => {
  const visibleFilters = facets.filter((filter) => {
    const attribute = filter.property;
    return filters?.[attribute];
  });
  const isSubscribedToCourseraLite = useOwnsCourseraLite();

  return (
    <div css={styles.mobileFiltersModal(isHidden)}>
      <div css={styles.contentContainer}>
        <div css={styles.header}>
          {/* eslint-disable-next-line @coursera-web/custom-rules/prefer-cds-typography2 -- FIXME: legacy; migrate to Typography2 */}
          <Typography variant="h2semibold">{_t('Filter by: ')}</Typography>
          <Button
            variant="ghost"
            icon={<CloseIcon size="small" />}
            iconPosition="after"
            onClick={toggleMobileFilterMenu}
          >
            {_t('Close')}
          </Button>
        </div>
        <AccordionGroup spacing={undefined}>
          {visibleFilters.map((filter) => {
            const attribute = filter.property;
            const name = filter.getNameOnMobile(isSubscribedToCourseraLite);
            const rawItems = filters?.[attribute] ?? [];

            // For cost filter, check if it should be hidden entirely using raw data
            // its done this way to prevent hydration issues if the filter is hidden
            const shouldHideCostFilter =
              attribute === COST_FACET_KEY &&
              (rawItems.length === 0 || (rawItems.length === 1 && rawItems[0]?.value === COST_FILTER_VALUE_PAID));

            const { supportText, supportTextProps } = getSupportPropsFromFilter(filter);

            return (
              <Accordion
                key={attribute}
                variant="silent"
                css={[
                  !!supportText && styles.hasSupportText,
                  shouldHideCostFilter &&
                    css`
                      display: none;
                    `,
                ]}
              >
                <AccordionHeader label={name} supportText={supportText} supportTextProps={supportTextProps} />

                <AccordionPanel>
                  <SearchFilterMobile
                    attribute={attribute}
                    filterName={name}
                    showCPlusSupportText={showCPlusSupportText}
                    items={rawItems}
                    addFilters={addFilters}
                    removeFilters={removeFilters}
                    eventingData={eventingData}
                  />
                </AccordionPanel>
              </Accordion>
            );
          })}
        </AccordionGroup>
      </div>
      <div css={styles.footer}>
        <Button variant="primary" onClick={toggleMobileFilterMenu}>
          {_t('Apply')}
        </Button>
        <Button variant="secondary" onClick={clearFilters}>
          {_t('Clear All')}
        </Button>
      </div>
    </div>
  );
};

export default memo(SearchFiltersMobileModal);
